import { Column, Row } from 'further-ui/components/Layout';
import { FC } from 'react';
import FormField from 'further-ui/components/FormField';
import NumberInput from 'further-ui/components/NumberInput';
import Switch from 'further-ui/components/Switch';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Controller, Control } from 'react-hook-form';
import type { TrancheFormInput } from '../../useTrancheForm';

type Props = {
  control: Control<TrancheFormInput>;
  currency: CurrencyCode;
};

const toNumber = (value: string): number | null =>
  value ? Number(value) : null;

const toString = (value: number | null): string =>
  value != null ? String(value) : '';

const DataSnapshot: FC<Props> = ({ control, currency }) => {
  return (
    <Column spacing="lg" fullWidth>
      <FormField label="Minimum direct subscription" size="md">
        <Controller
          name="minimumDirectInvestment"
          control={control}
          render={({ field, fieldState }) => (
            <NumberInput
              fullWidth
              size="small"
              placeholder="Enter amount"
              currency={currency}
              fixedDecimalScale
              decimals={2}
              currencyStartAdornment
              value={toNumber(field.value)}
              onChange={(val) => field.onChange(toString(val))}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Minimum advised subscription" size="md">
        <Controller
          name="minimumAdvisedInvestment"
          control={control}
          render={({ field, fieldState }) => (
            <NumberInput
              fullWidth
              size="small"
              placeholder="Enter amount"
              currency={currency}
              fixedDecimalScale
              decimals={2}
              currencyStartAdornment
              value={toNumber(field.value)}
              onChange={(val) => field.onChange(toString(val))}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FormField>
      <FormField
        label="Minimum includes advice fee"
        size="md"
        labelSize="lg"
        layout="horizontal"
      >
        <Controller
          name="minimumIncludesAdviceFee"
          control={control}
          render={({ field }) => (
            <Switch checked={field.value} onChange={field.onChange} />
          )}
        />
      </FormField>
      <FormField
        label="Income tax relief"
        size="md"
        tooltip="Enter the maximum income tax relief available to investors. This setting only applies to EIS, SEIS and VCT."
      >
        <Controller
          name="incomeTaxRebate"
          control={control}
          render={({ field, fieldState }) => (
            <NumberInput
              fullWidth
              size="small"
              placeholder="Enter percentage"
              fixedDecimalScale
              decimals={2}
              showPercentSuffix
              value={toNumber(field.value)}
              onChange={(val) => field.onChange(toString(val))}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FormField>
      <FormField size="md">
        <Row spacing="sm">
          <FormField
            tooltip="Enter the target return. This setting only applies to EIS, SEIS and LP."
            label="Target return"
            size="md"
          >
            <Controller
              name="targetReturn"
              control={control}
              render={({ field, fieldState }) => (
                <NumberInput
                  fullWidth
                  size="small"
                  placeholder="Enter amount"
                  value={toNumber(field.value)}
                  onChange={(val) => field.onChange(toString(val))}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </FormField>
          <FormField
            tooltip="Enter the target portfolio size. This setting only applies to EIS, SEIS and LP."
            label="Target portfolio size"
            size="md"
          >
            <Controller
              name="targetPortfolioSize"
              control={control}
              render={({ field, fieldState }) => (
                <NumberInput
                  fullWidth
                  size="small"
                  placeholder="Enter amount"
                  value={toNumber(field.value)}
                  onChange={(val) => field.onChange(toString(val))}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </FormField>
        </Row>
      </FormField>
    </Column>
  );
};

export default DataSnapshot;
